import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNodeConfigBase } from './node-config-base';
import { EntitySelectComponent, EntitySelectPage } from '../../../../../../../shared/components/entity-select/entity-select.component';
import { WorkflowVariablePickerComponent } from '../variable-picker/variable-picker.component';
import {
  UserDirectoryApiService,
  DirectoryUser,
  directoryUserLabel
} from '../../../../../../../shared/services/user-directory-api.service';
import { TicketsConfigApiService } from '../../../../../../tickets/services/tickets-config-api.service';
import { TicketsApiService } from '../../../../../../tickets/services/tickets-api.service';
import { Ticket } from '../../../../../../tickets/models/tickets.model';

/** Opción de un catálogo de tickets: clave que viaja al backend + etiqueta en español. */
interface CatalogOption { value: string; label: string; }

/**
 * ACTION → Tickets: CREATE_TICKET, CHANGE_TICKET_STATUS, CHANGE_TICKET_PRIORITY,
 * CHANGE_TICKET_PIPELINE y ASSIGN_TICKET.
 *
 * <p>El registro inscrito en el workflow es un contacto, una empresa o un negocio —nunca un
 * ticket—, así que las acciones que modifican un ticket existente piden cuál: por su número
 * `TKT-…` (lo que el usuario reconoce, y lo que suele venir en un token del paso anterior) o por
 * su id. «Crear ticket» no lo pide porque lo abre él mismo y lo asocia solo al registro del
 * workflow.</p>
 *
 * <p>Los catálogos viajan por NOMBRE, no por id: es lo que el backend empareja contra el catálogo
 * del negocio (`findStatusIdByName` y compañía) y lo que se puede escribir con un token.</p>
 */
@Component({
  selector: 'app-wf-ticket-action-config',
  standalone: true,
  imports: [CommonModule, SelectValueSyncDirective, EntitySelectComponent, WorkflowVariablePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (subtype()) {
      @case ('CREATE_TICKET') {
        <div class="space-y-4">
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Asunto</label>
              <app-wf-variable-picker [target]="subjectField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ subject: $event })" />
            </div>
            <input #subjectInput type="text" [class]="fieldClass" [value]="str('subject')" [disabled]="readOnly()"
                   (input)="patchConfig({ subject: inputValue($event) })" />
          </div>
          <div>
            <div class="mb-1.5 flex items-center justify-between">
              <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Descripción</label>
              <app-wf-variable-picker [target]="descriptionField()?.nativeElement ?? null" [disabled]="readOnly()"
                                      (valueChange)="patchConfig({ description: $event })" />
            </div>
            <textarea #descriptionInput rows="3" [class]="fieldClass" [value]="str('description')" [disabled]="readOnly()"
                      (input)="patchConfig({ description: inputValue($event) })"></textarea>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Prioridad</label>
              <select [class]="fieldClass" [value]="str('priorityName')" [disabled]="readOnly()"
                      (change)="patchConfig({ priorityName: inputValue($event) })">
                <option value="">Por defecto del catálogo</option>
                @for (p of priorities(); track p.value) { <option [value]="p.value">{{ p.label }}</option> }
              </select>
            </div>
            <div>
              <label [class]="labelClass">Categoría</label>
              <select [class]="fieldClass" [value]="str('categoryName')" [disabled]="readOnly()"
                      (change)="patchConfig({ categoryName: inputValue($event) })">
                <option value="">Sin categoría</option>
                @for (c of categories(); track c.value) { <option [value]="c.value">{{ c.label }}</option> }
              </select>
            </div>
          </div>
          <div>
            <label [class]="labelClass">Responsable (opcional)</label>
            <app-entity-select
              [value]="num('ownerUserId') || null" [displayText]="ownerName()"
              [searchFn]="userSearch" [labelFn]="userLabel" [sublabelFn]="userSublabel" [disabled]="readOnly()"
              placeholder="Buscar usuario…"
              (valueChange)="patchConfig({ ownerUserId: $event })"></app-entity-select>
            <p [class]="hintClass">Si lo dejas vacío, se aplican las reglas de asignación de Tickets.</p>
          </div>
          <p [class]="hintClass">
            El ticket se asocia automáticamente al registro del workflow (contacto, empresa o negocio).
          </p>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('ASSIGN_TICKET') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="ticketTarget" />
          <div>
            <label [class]="labelClass">Nuevo responsable</label>
            <app-entity-select
              [value]="num('ownerUserId') || null" [displayText]="ownerName()"
              [searchFn]="userSearch" [labelFn]="userLabel" [sublabelFn]="userSublabel" [disabled]="readOnly()"
              placeholder="Buscar usuario…"
              (valueChange)="patchConfig({ ownerUserId: $event })"></app-entity-select>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('CHANGE_TICKET_STATUS') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="ticketTarget" />
          <div>
            <label [class]="labelClass">Estado destino</label>
            <select [class]="fieldClass" [value]="str('statusName')" [disabled]="readOnly()"
                    (change)="patchConfig({ statusName: inputValue($event) })">
              <option value="">Elige un estado…</option>
              @for (s of statuses(); track s.value) { <option [value]="s.value">{{ s.label }}</option> }
            </select>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('CHANGE_TICKET_PRIORITY') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="ticketTarget" />
          <div>
            <label [class]="labelClass">Prioridad destino</label>
            <select [class]="fieldClass" [value]="str('priorityName')" [disabled]="readOnly()"
                    (change)="patchConfig({ priorityName: inputValue($event) })">
              <option value="">Elige una prioridad…</option>
              @for (p of priorities(); track p.value) { <option [value]="p.value">{{ p.label }}</option> }
            </select>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('CHANGE_TICKET_PIPELINE') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="ticketTarget" />
          <div>
            <label [class]="labelClass">Pipeline destino</label>
            <select [class]="fieldClass" [value]="str('pipelineName')" [disabled]="readOnly()"
                    (change)="patchConfig({ pipelineName: inputValue($event) })">
              <option value="">Elige un pipeline…</option>
              @for (p of pipelines(); track p.value) { <option [value]="p.value">{{ p.label }}</option> }
            </select>
            <p [class]="hintClass">El ticket se colocará en la primera etapa del pipeline elegido.</p>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @default {
        <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
          Esta acción de tickets no tiene campos configurables.
        </p>
      }
    }

    <ng-template #ticketTarget>
      <div>
        <label [class]="labelClass">Cómo se indica el ticket</label>
        <select [class]="fieldClass" [value]="ticketMode()" [disabled]="readOnly()"
                (change)="setTicketMode(inputValue($event))">
          <option value="SEARCH">Elegir un ticket concreto</option>
          <option value="VARIABLE">Tomarlo de una variable</option>
        </select>
      </div>
      @if (ticketMode() === 'SEARCH') {
        <div>
          <label [class]="labelClass">Ticket</label>
          <!-- Buscador por asunto o número: el usuario no conoce el id interno. -->
          <app-entity-select
            [value]="num('ticketId') || null" [displayText]="ticketName()"
            [searchFn]="ticketSearch" [labelFn]="ticketLabel" [sublabelFn]="ticketSublabel" [disabled]="readOnly()"
            placeholder="Buscar por asunto o número…"
            (valueChange)="patchConfig({ ticketId: $event, ticketNumber: '' })"
            (selected)="onTicketSelected($event)"></app-entity-select>
        </div>
      } @else {
        <div>
          <div class="mb-1.5 flex items-center justify-between">
            <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Número del ticket</label>
            <app-wf-variable-picker [target]="ticketNumberField()?.nativeElement ?? null" [disabled]="readOnly()"
                                    (valueChange)="patchConfig({ ticketNumber: $event, ticketId: null })" />
          </div>
          <input #ticketNumberInput type="text" [class]="fieldClass" [value]="str('ticketNumber')" [disabled]="readOnly()"
                 placeholder="{{ '{{ticket.ticketNumber}}' }}"
                 (input)="patchConfig({ ticketNumber: inputValue($event), ticketId: null })" />
          <p [class]="hintClass">
            Útil cuando el ticket lo trae el propio workflow: el del registro inscrito, o el que
            devolvió un paso «Crear ticket» anterior.
          </p>
        </div>
      }
    </ng-template>

    <ng-template #continueOnError>
      <label class="flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-300">
        <input type="checkbox" class="rounded border-gray-300" [checked]="bool('continueOnError')" [disabled]="readOnly()"
               (change)="patchConfig({ continueOnError: checkboxValue($event) })" />
        Continuar el flujo si esta acción falla
      </label>
    </ng-template>
  `
})
export class WorkflowTicketActionConfigComponent extends WorkflowNodeConfigBase {
  private readonly directory = inject(UserDirectoryApiService);
  private readonly ticketCatalog = inject(TicketsConfigApiService);
  private readonly ticketsApi = inject(TicketsApiService);

  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');

  /** `value` = lo que empareja el backend; `label` = el texto en español que ve el usuario. */
  protected readonly statuses = signal<CatalogOption[]>([]);
  protected readonly priorities = signal<CatalogOption[]>([]);
  protected readonly categories = signal<CatalogOption[]>([]);
  protected readonly pipelines = signal<CatalogOption[]>([]);

  /** Nombre del responsable ya guardado, para que al reabrir el nodo no aparezca vacío. */
  protected readonly ownerName = computed(() => {
    const id = this.num('ownerUserId');
    if (!id) {
      return '';
    }
    const user = this.directory.knownUsers().get(id);
    return user ? directoryUserLabel(user) : `Usuario #${id}`;
  });

  constructor() {
    super();
    effect(() => this.directory.resolveUsers([this.num('ownerUserId')]));
    this.ticketCatalog.getStatuses().subscribe({
      next: (list) => this.statuses.set(this.options(list)),
      error: () => {}
    });
    this.ticketCatalog.getPriorities().subscribe({
      next: (list) => this.priorities.set(this.options(list)),
      error: () => {}
    });
    this.ticketCatalog.getCategories().subscribe({
      next: (list) => this.categories.set(this.options(list)),
      error: () => {}
    });
    this.ticketCatalog.getPipelines().subscribe({
      next: (list) => this.pipelines.set(this.options(list)),
      error: () => {}
    });
  }

  /**
   * El backend empareja por NOMBRE (no por id), y acepta tanto la clave técnica como la etiqueta.
   * Se guarda la clave y se muestra la etiqueta en español: los catálogos de tickets tienen el
   * `name` en inglés (NEW, HIGH…) y el `label` traducido, y enseñar el `name` dejaba el
   * desplegable en inglés.
   */
  private options(list: readonly { name?: string | null; label?: string | null }[] | null | undefined): CatalogOption[] {
    return (list ?? [])
      .filter((item): item is { name: string; label?: string | null } => !!item?.name)
      .map((item) => ({ value: item.name, label: item.label || item.name }));
  }

  /**
   * Cómo se indica el ticket destino. No se guarda: se deduce de cuál de los dos campos tiene
   * valor, igual que la modalidad de la reunión. Por defecto se ofrece el buscador, que es lo
   * que espera quien configura el paso a mano; en cuanto hay un `ticketNumber` (típicamente un
   * token) se muestra el modo variable para no ocultarle lo que ya configuró.
   */
  protected ticketMode(): 'SEARCH' | 'VARIABLE' {
    return this.str('ticketNumber') ? 'VARIABLE' : 'SEARCH';
  }

  protected setTicketMode(modo: string): void {
    if (modo === 'VARIABLE') {
      this.patchConfig({ ticketId: null, ticketNumber: this.str('ticketNumber') || '{{ticket.ticketNumber}}' });
    } else {
      this.patchConfig({ ticketNumber: '' });
    }
  }

  /** Nombre del ticket ya elegido, para que al reabrir el nodo no aparezca el selector vacío. */
  protected readonly ticketName = signal('');

  protected onTicketSelected(ticket: Ticket | null): void {
    this.ticketName.set(ticket ? this.ticketLabel(ticket) : '');
  }

  ticketSearch = (term: string): Observable<EntitySelectPage<Ticket>> =>
    this.ticketsApi.quickSearchTickets(term).pipe(
      map((list) => ({ items: list ?? [], totalPages: 1 })),
      catchError(() => of({ items: [] as Ticket[], totalPages: 0 }))
    );
  ticketLabel = (t: Ticket): string => t.subject || t.ticketNumber || `Ticket #${t.id}`;
  ticketSublabel = (t: Ticket): string => t.ticketNumber ?? '';

  // Campos destino del selector de variables. El @switch deja visible uno solo cada vez.
  protected readonly subjectField = viewChild<ElementRef<HTMLInputElement>>('subjectInput');
  protected readonly descriptionField = viewChild<ElementRef<HTMLTextAreaElement>>('descriptionInput');
  protected readonly ticketNumberField = viewChild<ElementRef<HTMLInputElement>>('ticketNumberInput');

  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.directory.searchUsers(term, 20).pipe(
      map((list) => ({ items: list, totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );
  userLabel = (user: DirectoryUser): string => directoryUserLabel(user);
  userSublabel = (user: DirectoryUser): string => user.mail ?? '';
}
